import Notification from "@/types/Notification.interface"
import Pagination from "@/types/Pagination.interface"
import { NotificationParams } from "@/types/PaginationParams.interface"

interface State {
    notifications: Notification[]
    notificationsLastPage: number,
    notificationsPaginating: boolean,
    notificationsParams: NotificationParams,
    notificationsSelectedNotification: Notification
}

const defaults = () => {
    return {
        notifications: [],
        notificationsLastPage: null,
        notificationsPaginating: false,
        notificationsParams: { page: 1, type: 'all' },
        notificationsSelectedNotification: null
    }
}
export default {
    state: defaults(),
    mutations: {
        notificationsPaginatingSet(state: State, bool: boolean): void {
            state.notificationsPaginating = bool
        },
        notificationsPush(state: State, notifications: Notification[]): void {
            //  Add our notifications and remove any dupes
            state.notifications = window.removeDuplicatesById(state.notifications, notifications)

            //  Sort our notifications by their created_at
            state.notifications.sort((a: Notification, b: Notification) => {
                //  @ts-ignore
                return +window.getDate(b.created_at) - +window.getDate(a.created_at)
            })
        },
        notificationsParamsPush(state: State, page: Pagination) {
            //  If this is page one, reset our list
            if (page.current_page === 1) state.notifications = []

            //  Set the notification pagination params
            state.notificationsLastPage = page.last_page
        },
        notificationsParamsSet(state: State, params: NotificationParams): void {
            state.notificationsParams = params
        },
        notificationsRemove(state: State, ids: string[]): void {
            //  Get our global notifications list
            const notifications = state.notifications

            //  If no notifications, do nothing
            if (!notifications) return

            //  Else, filter out our removed ids
            const filtered = notifications.filter((x: Notification) => !ids.includes(x.id))

            //  Replace our list with the filtered list
            state.notifications = filtered
        },
        notificationsSelectedNotificationSet(state: State, notification: Notification): void {
            state.notificationsSelectedNotification = notification
        },
        notificationsSet(state: State, notifications: Notification[]): void {
            state.notifications = notifications
        },
        notificationsReset(state) {
            Object.assign(state, defaults())
        }
    },
    actions: {
        notificationsDelete({ commit, getters }, ids: string[]): Promise<string[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/notifications/delete`

            //  Create our payload from our params passed in
            const payload = { ids: ids }

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                return response.data
            }).catch(() => null)
        },
        notificationsDismiss({ getters }, ids: string[]): Promise<string[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/notifications/dismiss`

            //  Create our payload from our params passed in
            const payload = { ids: ids }

            //  Send the request
            return window.axios.put(url, payload).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        notificationsDismissAll({ getters }): Promise<string[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/notifications/dismiss/all`

            //  Send the request
            return window.axios.put(url).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        notificationsPaginate({ commit, getters }): Promise<Pagination> {
            //  Grabs our pagiantion params
            const p = getters.notificationsParams

            //  Assemble the API endpoint
            const url = `${getters.api}/notifications?${window.toUrlParams(p)}`

            //  Start the pagination tracker
            commit('notificationsPaginatingSet', true)

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination params
                commit('notificationsParamsPush', response.data)

                //  Update the list
                commit('notificationsPush', response.data.data)

                //  Stop the pagination tracker
                commit('notificationsPaginatingSet', false)

                //  Return our pagination data
                return response.data
            }).catch(() => null)
        },
        notificationsReset({ getters }, ids: string[]): Promise<Pagination> {
            //  Assemble the API endpoint
            const url = `${getters.api}/notifications/reset`

            //  Create our payload from our params passed in
            const payload = { ids: ids }

            //  Send the request
            return window.axios.put(url, payload).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        notificationsSelectedNotificationGet({ commit, getters }, id: string): Promise<Notification> {
            //  Assemble the API endpoint
            const url = `${getters.api}/notifications/${id}`

            //  Send the request
            return window.axios.get(url).then(response => {

                //  Add the notification to our list
                commit('notificationsPush', [response.data])

                //  Set our selected notification
                commit('notificationsSelectedNotificationSet', response.data)

                //  Return our found notification
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        notifications(state: State): Notification[] {
            return state.notifications
        },
        notificationsLastPage(state: State): number {
            return state.notificationsLastPage
        },
        notificationsPaginating(state: State): boolean {
            return state.notificationsPaginating
        },
        notificationsParams(state: State): NotificationParams {
            return state.notificationsParams
        },
        notificationsSelectedNotification(state: State): Notification {
            return state.notificationsSelectedNotification
        }
    }
}
